import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Animated, Platform, Alert } from 'react-native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useRef as useReactRef } from 'react';
import type { WindowComponentType } from '../Components/UnifiedWindow';
import type { ActionHierarchyHandle } from '@components/Hierarchy/ActionHierarchy';
import { calculateWindowWidth } from '../utils/windowHandlers';
import { useReactiveWindowDimensions } from '@hooks/useReactiveWindowDimensions';

export const useEasyModeWindow = () => {
  // Unified window state - single window for all components
  const [activeWindowComponent, setActiveWindowComponent] = useState<WindowComponentType>(null);
  const { width: screenWidth } = useReactiveWindowDimensions();
  const [windowLoading, setWindowLoading] = useState(false);
  const windowWidthAnim = useRef(new Animated.Value(0)).current;

  // Component-specific refs and state
  const hierarchyRef = useReactRef<ActionHierarchyHandle>(null);
  const reduxActions = useSelector((state: DefaultRootState) => state.actionMaker.present.actions) || [];
  const [hasHierarchyActions, setHasHierarchyActions] = useState(false);

  // Calculate window width using utility (imported function)
  const windowWidth = useMemo(() => calculateWindowWidth(screenWidth), [screenWidth]);

  // Computed values for window visibility (for backward compatibility with existing code)
  const showHierarchy = activeWindowComponent === 'hierarchy';
  const showCanvas = activeWindowComponent === 'canvas';
  const showEditor = activeWindowComponent === 'editor';
  const showDataHandler = activeWindowComponent === 'dataHandler';
  const showDocument = activeWindowComponent === 'document';

  // Check if hierarchy has actions
  useEffect(() => {
    if (hierarchyRef.current) {
      const aiActions = hierarchyRef.current.getAIManagedActions() || [];
      const hasActions = aiActions.length > 0 || reduxActions.length > 0;
      setHasHierarchyActions(hasActions);
    } else {
      setHasHierarchyActions(reduxActions.length > 0);
    }
  }, [reduxActions, activeWindowComponent]);

  // Animate unified window in/out with lazy loading
  useEffect(() => {
    if (activeWindowComponent) {
      const targetWidth = windowWidth;
      // Start loading when window opens
      setWindowLoading(true);
      // Simulate component loading (replace with actual lazy load)
      setTimeout(() => {
        setWindowLoading(false);
      }, 300);
      Animated.timing(windowWidthAnim, {
        toValue: targetWidth,
        duration: 300,
        useNativeDriver: false, // width requires layout animation
      }).start();
    } else {
      Animated.timing(windowWidthAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
      setWindowLoading(false);
    }
  }, [activeWindowComponent, windowWidthAnim, windowWidth]);

  // Handle unified window toggle - all components use the same window
  const handleToggleWindow = useCallback((componentType: WindowComponentType) => {
    setActiveWindowComponent(prev => {
      // If clicking the same component, close the window
      if (prev === componentType) {
        return null;
      }
      // Otherwise, switch to the new component
      return componentType;
    });
  }, []);

  // Convenience handlers for each component type
  const handleToggleHierarchy = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Alert.alert(
        'Coming Soon',
        'This feature is coming soon!',
        [{ text: 'OK' }]
      );
      return;
    }
    handleToggleWindow('hierarchy');
  }, [handleToggleWindow]);

  const handleToggleCanvas = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Alert.alert(
        'Coming Soon',
        'This feature is coming soon!',
        [{ text: 'OK' }]
      );
      return;
    }
    handleToggleWindow('canvas');
  }, [handleToggleWindow]);

  const handleToggleEditor = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Alert.alert(
        'Coming Soon',
        'This feature is coming soon!',
        [{ text: 'OK' }]
      );
      return;
    }
    handleToggleWindow('editor');
  }, [handleToggleWindow]);

  const handleToggleDataHandler = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Alert.alert(
        'Coming Soon',
        'This feature is coming soon!',
        [{ text: 'OK' }]
      );
      return;
    }
    handleToggleWindow('dataHandler');
  }, [handleToggleWindow]);

  const handleToggleDocument = useCallback(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Alert.alert(
        'Coming Soon',
        'This feature is coming soon!',
        [{ text: 'OK' }]
      );
      return;
    }
    handleToggleWindow('document');
  }, [handleToggleWindow]);

  return {
    activeWindowComponent,
    setActiveWindowComponent,
    windowLoading,
    setWindowLoading,
    windowWidthAnim,
    windowWidth,
    showHierarchy,
    showCanvas,
    showEditor,
    showDataHandler,
    showDocument,
    hierarchyRef,
    hasHierarchyActions,
    setHasHierarchyActions,
    handleToggleWindow,
    handleToggleHierarchy,
    handleToggleCanvas,
    handleToggleEditor,
    handleToggleDataHandler,
    handleToggleDocument,
  };
};


